/**
 * MH-21 Tiếp sóng và tiếp âm (mục 6.5 đặc tả giao diện).
 *
 * Danh mục nguồn tiếp sóng/tiếp âm với trạng thái luồng đang thu và mức âm
 * THỜI GIAN THỰC (Redis, không phải bảng quan hệ — xem `nguon-tiep-song.service.ts`),
 * để phát hiện luồng im lặng hoặc mất tín hiệu trước khi người dân phản ánh.
 * Nguyên tắc bắt buộc: thu MỘT bản ở cấp tỉnh rồi phân phối lại, không để từng
 * xã tự thu cùng một luồng — luôn hiển thị dòng ghi chú này.
 *
 * Chưa có API tra cứu theo trang cho danh mục này (`danhSachNguonTiepSong` trả
 * toàn bộ mảng, không phân trang) — số nguồn tiếp sóng của một tỉnh nhỏ (vài
 * chục), nên không vi phạm ngưỡng hiệu năng của chương 12; đã ghi vào
 * NOTES-A0.md nếu về sau cần phân trang.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { vi } from '../ngon-ngu/vi';
import { phatSongApi, type DongNguonTiepSong, type NguonTiepSongVao } from '../api/phat-song-api';
import { dungTaiDuLieu, dungTuLamMoi } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong } from '../thanh-phan/TrangThaiManHinh';

export interface MH21Props {
  api?: Pick<typeof phatSongApi, 'danhSachNguonTiepSong' | 'khaiBaoNguonTiepSong' | 'batDauTiepSong'>;
}

const LOAI_NHAN: Record<DongNguonTiepSong['loai'], string> = {
  hls: vi.phatSong.loaiHls,
  icy: vi.phatSong.loaiIcy,
  rtmp: vi.phatSong.loaiRtmp,
  fm: vi.phatSong.loaiFm,
};

export function MH21TiepSong({ api }: MH21Props = {}): ReactNode {
  const goi = api ?? phatSongApi;
  const [moKhaiBao, datMoKhaiBao] = useState(false);
  const [ten, datTen] = useState('');
  const [loai, datLoai] = useState<DongNguonTiepSong['loai']>('hls');
  const [diaChi, datDiaChi] = useState('');
  const [dangGui, datDangGui] = useState(false);
  const [dangBatDau, datDangBatDau] = useState<string | null>(null);

  const kq = dungTaiDuLieu<DongNguonTiepSong[]>({
    tai: () => goi.danhSachNguonTiepSong() as Promise<DongNguonTiepSong[]>,
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  // Trạng thái luồng đang thu là thời gian thực — làm mới định kỳ (≥5s, đúng
  // ngưỡng chương 12; đây KHÔNG phải MH-20 nên không được phép nhanh hơn).
  dungTuLamMoi(kq.taiLai, { chuKyMs: 8_000, dangChonDong: false, dangMoNgan: moKhaiBao });

  const khaiBao = useCallback(async (): Promise<void> => {
    datDangGui(true);
    try {
      await goi.khaiBaoNguonTiepSong({ ten, loai, diaChi } satisfies NguonTiepSongVao);
      datMoKhaiBao(false);
      datTen('');
      datDiaChi('');
      kq.taiLai();
    } finally {
      datDangGui(false);
    }
  }, [goi, ten, loai, diaChi, kq]);

  const batDau = useCallback(
    async (nguon: DongNguonTiepSong): Promise<void> => {
      datDangBatDau(nguon.id);
      try {
        const phamVi = (nguon.phamViApDung ?? []).map((ma) => ({ DichID: ma }));
        await goi.batDauTiepSong(phamVi, nguon.id);
        kq.taiLai();
      } finally {
        datDangBatDau(null);
      }
    },
    [goi, kq],
  );

  return (
    <div className="man-hinh" data-man-hinh="MH-21">
      <div className="man-hinh__dau">
        <h1>{vi.phatSong.tiepSongTieuDe}</h1>
        <div className="man-hinh__hanh-dong">
          <button type="button" className="nut nut--chinh" onClick={() => datMoKhaiBao(true)}>
            {vi.phatSong.khaiBaoNguonMoi}
          </button>
        </div>
      </div>
      <p data-vung="ghi-chu-tiep-song">{vi.phatSong.tiepSongGhiChu}</p>

      {kq.trangThai === 'dang-tai' ? <KhungXuong soDong={4} soCot={6} taiLau={kq.taiLau} /> : null}
      {kq.trangThai === 'loi' && kq.loi ? <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} /> : null}
      {kq.trangThai === 'rong' ? <TrangThaiRong moTa={vi.phatSong.rongMoTaTiepSong} /> : null}

      {kq.trangThai === 'co-du-lieu' && kq.duLieu ? (
        <table className="bang" data-vung="danh-sach-nguon-tiep-song">
          <thead>
            <tr>
              <th scope="col">{vi.phatSong.tenNguon}</th>
              <th scope="col">{vi.phatSong.loaiNguon}</th>
              <th scope="col">{vi.phatSong.diaChiNguon}</th>
              <th scope="col">{vi.phatSong.trangThaiThu}</th>
              <th scope="col">{vi.phatSong.mucAm}</th>
              <th scope="col">{vi.banTin.cot.hanhDong}</th>
            </tr>
          </thead>
          <tbody>
            {kq.duLieu.map((n) => (
              <tr key={n.id} data-im-lang={n.imLang === true}>
                <td>{n.ten}</td>
                <td>{LOAI_NHAN[n.loai]}</td>
                <td>{n.diaChi}</td>
                <td>
                  <span className={`nhan-tt nhan-tt--${n.dangThu ? 'phat' : 'tam-dung'}`}>
                    {n.dangThu ? vi.phatSong.dangThu : vi.phatSong.khongThu}
                  </span>
                  {n.imLang ? (
                    <span role="alert" data-vung="canh-bao-im-lang">
                      {' '}
                      {vi.phatSong.canhBaoImLang}
                    </span>
                  ) : null}
                </td>
                <td>{n.mucAmLuongDb !== null ? `${n.mucAmLuongDb} dB` : vi.banTin.chuaCo}</td>
                <td>
                  <button
                    type="button"
                    className="nut nut--lien-ket"
                    disabled={dangBatDau === n.id}
                    onClick={() => void batDau(n)}
                  >
                    {vi.phatSong.batDauTiepSong}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {moKhaiBao ? (
        <div className="man-che" role="presentation">
          <div className="hop-thoai" role="dialog" aria-modal="true" aria-label={vi.phatSong.khaiBaoNguonMoi}>
            <p className="truong">
              <label htmlFor="ts-ten">{vi.phatSong.tenNguon}</label>
              <input id="ts-ten" value={ten} onChange={(e) => datTen(e.target.value)} />
            </p>

            <p className="truong">
              <label htmlFor="ts-loai">{vi.phatSong.loaiNguon}</label>
              <select id="ts-loai" value={loai} onChange={(e) => datLoai(e.target.value as DongNguonTiepSong['loai'])}>
                <option value="hls">{vi.phatSong.loaiHls}</option>
                <option value="icy">{vi.phatSong.loaiIcy}</option>
                <option value="rtmp">{vi.phatSong.loaiRtmp}</option>
                <option value="fm">{vi.phatSong.loaiFm}</option>
              </select>
            </p>

            <p className="truong">
              <label htmlFor="ts-dia-chi">{vi.phatSong.diaChiNguon}</label>
              <input id="ts-dia-chi" value={diaChi} onChange={(e) => datDiaChi(e.target.value)} />
            </p>

            <div className="hop-thoai__nut">
              <button type="button" className="nut" onClick={() => datMoKhaiBao(false)}>
                {vi.phatSong.huy}
              </button>
              <button
                type="button"
                className="nut nut--chinh"
                disabled={dangGui || !ten.trim() || !diaChi.trim()}
                onClick={() => void khaiBao()}
              >
                {vi.phatSong.khaiBaoNguonMoi}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
